import { map, sortBy } from 'lodash';
import React, { FC, useCallback } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import { AnswerCondition as AnswerConditionType } from 'src/actions/rules/condition';
import answerLanguagesResource from 'src/actions/answers/answerLanguagesResource';
import answerSearchResource from 'src/actions/answers/answerSearchResource';
import { AnswerType } from 'src/actions/rules/actions';
import AsyncDropdown from 'src/presentation/components/forms/AsyncDropdown';
import DeleteButton from 'src/presentation/components/pages/shared/DeleteButton';
import { default as Languages } from '@ad-scripts/domain/models/languages/answer_languages';
import { StyledDropdown, StyledEntityRow, StyledRowItem, StyledTypeDescription } from '../shared/StyledComponents';
import useCurrentAnswer from 'src/presentation/components/pages/shared/hooks/useCurrentAnswer';
import useDocumentResource from 'src/presentation/hooks/useDocumentResource';

type AnswerConditionProps = {
  condition: AnswerConditionType;
  index: number;
  onDelete: Function;
  onUpdate: Function;
};

const AnswerCondition: FC<AnswerConditionProps> = ({ condition, index, onDelete, onUpdate }) => {
  const dispatch = useDispatch();
  const searchForAnswer = answerSearchResource.effects.request;

  const onAnswerIdChange = useCallback(
    answerId => {
      condition.updateAnswerId(answerId);
      onUpdate({ condition, index });
    },
    [condition, index, onUpdate]
  );

  const onAnswerChannelChange = useCallback(
    channelId => {
      condition.updateAnswerChannel(channelId);
      onUpdate({ condition, index });
    },
    [condition, index, onUpdate]
  );

  const onAnswerLanguageChange = useCallback(
    languageId => {
      condition.updateAnswerLanguage(languageId);
      onUpdate({ condition, index });
    },
    [condition, index, onUpdate]
  );

  const onSearchAnswers = useCallback(
    text => {
      dispatch(searchForAnswer({ text }));
    },
    [dispatch, searchForAnswer]
  );

  const { loadingState: languagesLoading, value } = useDocumentResource(answerLanguagesResource);
  const answerLanguages = sortBy(
    map(value, lang => ({ value: lang, label: Languages[lang] || '' })),
    v => v.label
  );

  // only a single answerId is allowed in the condition
  const currentAnswerId = condition.answerIds.length === 1 ? condition.answerIds[0] : null;
  const { loadingState: answerLoading, value: currentAnswer } = useCurrentAnswer(currentAnswerId);
  const answerOptions = useAnswerOptions(currentAnswer);

  if (languagesLoading !== 'succeeded' && answerLoading !== 'succeeded') {
    return null;
  }

  return (
    <>
      <AnswerEntityRow>
        <StyledRowItem>And</StyledRowItem>
        <StyledTypeDescription>Answer name</StyledTypeDescription>
        <AnswerDescriptor>is</AnswerDescriptor>
        <AnswerSearchDropdown
          maxRows={5}
          value={currentAnswerId}
          options={answerOptions}
          onChange={onAnswerIdChange}
          onSearch={onSearchAnswers}
          text={!currentAnswerId ? 'Any' : ''}
          placeholder="Search for answer"
        />
        <DeleteButton id={index} onClick={onDelete} />
      </AnswerEntityRow>
      <SpacedAnswerEntityRow>
        <StyledRowItem> </StyledRowItem>
        <StyledTypeDescription>Language</StyledTypeDescription>
        <AnswerDescriptor>is</AnswerDescriptor>
        <AnswerMultiSelectDropDown
          isMultiSelect
          leftMargin
          maxRows={5}
          multiSelectLabel="languages"
          onSelect={onAnswerLanguageChange}
          options={answerLanguages}
          placeholder="Any"
          searchable
          value={condition.answerLanguages}
        />
      </SpacedAnswerEntityRow>
      <SpacedAnswerEntityRow>
        <StyledRowItem> </StyledRowItem>
        <StyledTypeDescription>Channel</StyledTypeDescription>
        <AnswerDescriptor>is</AnswerDescriptor>
        <AnswerMultiSelectDropDown
          isMultiSelect
          leftMargin
          maxRows={5}
          multiSelectLabel="channels"
          onSelect={onAnswerChannelChange}
          options={answerChannelOptions}
          placeholder="Any"
          value={condition.answerChannels}
        />
      </SpacedAnswerEntityRow>
    </>
  );
};

function useAnswerOptions(currentAnswer) {
  const { value: answerSearchResults } = useDocumentResource(answerSearchResource, { fetch: false });
  let containsCurrentAnswer = !(currentAnswer && currentAnswer.id);

  const answerOptions = answerSearchResults.map(answer => {
    if (!containsCurrentAnswer && answer.id === currentAnswer.id) {
      containsCurrentAnswer = true;
    }

    return {
      label: answer.name,
      value: answer.id,
    };
  });

  if (!containsCurrentAnswer) {
    answerOptions.push({ label: currentAnswer.name, value: currentAnswer.id });
  }

  return answerOptions;
}

const answerChannelOptions: { label: string; value: string }[] = [
  { label: 'Email', value: AnswerType.AnyChannel },
  { label: 'Messaging', value: AnswerType.Message },
  { label: 'Public', value: AnswerType.SelfService },
  { label: 'Reference', value: AnswerType.Info },
];

const AnswerDescriptor = styled.div`
  margin-left: 8px;
  margin-right: 16px;
`;

const AnswerEntityRow = styled(StyledEntityRow)`
  display: inline-flex;
`;

const AnswerMultiSelectDropDown = styled(StyledDropdown)`
  margin-left: 0;
`;

const AnswerSearchDropdown = styled(AsyncDropdown)`
  margin-right: 12px;
  width: 220px;
`;

const SpacedAnswerEntityRow = styled(StyledEntityRow)`
  padding-left: 24px;
  margin-top: 8px;
`;

export default AnswerCondition;
